import type { paths } from '@ccp/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client.js';
import { toApiError } from '../../../api/errors.js';

/**
 * Operator access, as the tenant sees and controls it.
 *
 * The types come from the OpenAPI document, so a field this screen reads is a field the API
 * actually returns; `generate-api-client --check` runs in CI.
 */
type Json<T> = T extends { content: { 'application/json': infer B } } ? B : never;

export type SupportGrant = Json<
  paths['/v1/support-grants']['get']['responses'][200]
>['items'][number];

export type ActiveGrants = Json<paths['/v1/support-grants/active']['get']['responses'][200]>;

export type SupportAccessPolicy = Json<paths['/v1/support-grants/policy']['get']['responses'][200]>;

export const grantKeys = {
  list: ['support-grants', 'list'] as const,
  active: ['support-grants', 'active'] as const,
  policy: ['support-grants', 'policy'] as const,
};

/** Every grant, with the reasons and the approvers. Needs `users.read`. */
export function useSupportGrants() {
  return useQuery({
    queryKey: grantKeys.list,
    queryFn: async () => {
      const { data, response } = await api.GET('/v1/support-grants', {});
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

/**
 * Whether an operator is inside this tenant right now.
 *
 * Every signed-in user may ask — the permission is `self.read` — because a banner only the owner
 * could see would not be a disclosure. Polled rather than pushed for now: the realtime channel
 * arrives with the workspace shell, and a grant lasts an hour.
 *
 * MVP-SHORTCUT: polled every half minute instead of pushed; V1: the realtime channel carries it
 */
export function useActiveGrants(options: { readonly enabled?: boolean } = {}) {
  return useQuery({
    queryKey: grantKeys.active,
    refetchInterval: 30_000,
    enabled: options.enabled ?? true,
    queryFn: async () => {
      const { data, response } = await api.GET('/v1/support-grants/active', {});
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

export function useSupportAccessPolicy() {
  return useQuery({
    queryKey: grantKeys.policy,
    queryFn: async () => {
      const { data, response } = await api.GET('/v1/support-grants/policy', {});
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
  });
}

export function useApproveGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, response } = await api.POST('/v1/support-grants/{id}/approve', {
        params: { path: { id } },
      });
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-grants'] }),
  });
}

export function useRevokeGrant() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { readonly id: string; readonly reason?: string }) => {
      const { data, response } = await api.POST('/v1/support-grants/{id}/revoke', {
        params: { path: { id: input.id } },
        body: input.reason === undefined ? {} : { reason: input.reason },
      });
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-grants'] }),
  });
}

export function useSetSupportAccessPolicy() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly approvalRequired: boolean;
      readonly maxDurationSeconds: number;
    }) => {
      const { data, response } = await api.PUT('/v1/support-grants/policy', { body: input });
      if (data === undefined) {
        throw toApiError(response.status, undefined);
      }
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-grants'] }),
  });
}
